import React from 'react';
import { motion } from 'motion/react';
import { PictureInPicture2, ArrowUpRight, ShieldCheck, Sparkles } from 'lucide-react';
import { useTranslation } from '../lib/translations';

interface PiPActivePlaceholderProps {
  onRestore: () => void;
  title?: string;
  subtitle?: string;
  gameState?: any;
  myId?: string;
}

export const PiPActivePlaceholder: React.FC<PiPActivePlaceholderProps> = ({
  onRestore,
  title,
  subtitle,
  gameState,
  myId,
}) => {
  const { language } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 bg-[#07090e] text-white flex flex-col items-center justify-center p-6 text-center select-none overflow-hidden">
      {/* Static GPU-friendly ambient background (no continuous re-rasterization) */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(6,182,212,0.12),transparent_70%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(99,102,241,0.08),transparent_50%)] pointer-events-none" />

      {/* Main card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative z-10 max-w-lg w-full bg-slate-900/95 border border-cyan-500/30 rounded-3xl p-8 shadow-2xl flex flex-col items-center"
      >
        {/* PiP icon */}
        <div className="relative mb-5">
          <div className="w-20 h-20 rounded-2xl bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center shadow-inner">
            <PictureInPicture2 className="w-10 h-10 text-cyan-400" />
          </div>
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-slate-900 flex items-center justify-center shadow-md">
            <span className="w-2 h-2 rounded-full bg-white" />
          </div>
        </div>

        {/* Status Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-black tracking-widest uppercase mb-3">
          <Sparkles className="w-3.5 h-3.5" />
          {language === 'tr' ? 'MİNİ PENCERE AKTİF' : 'MINI WINDOW ACTIVE'}
        </div>

        <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-2 italic">
          {title || (language === 'tr' ? 'Oyun Mini Pencerede Çalışıyor' : 'Running in Mini Window')}
        </h2>

        <p className="text-sm text-slate-300 mb-6 max-w-md leading-relaxed font-medium">
          {subtitle || (language === 'tr'
            ? 'Uygulama ekranınızın köşesindeki bağımsız mini pencereye taşındı. Diğer işlerinizi yaparken hamleleri ve masayı oradan yönetebilirsiniz.'
            : 'The application is running in an independent overlay window on your screen. You can control your moves and table while multitasking.')}
        </p>

        <div className="flex items-center gap-2 text-xs text-slate-400 mb-6 bg-black/40 px-4 py-2 rounded-xl border border-white/5">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{language === 'tr' ? 'Tüm oyun verileri ve bağlantı canlı olarak korunmaktadır.' : 'Game session and network sync are actively maintained.'}</span>
        </div>

        {/* Restore button */}
        <button
          onClick={onRestore}
          className="group relative cursor-pointer px-8 py-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 hover:from-cyan-400 hover:via-blue-500 hover:to-indigo-500 text-white font-black text-sm uppercase tracking-widest rounded-2xl shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all flex items-center gap-3 active:scale-95"
        >
          <span>{language === 'tr' ? 'Ana Sekmeye Geri Al' : 'Restore to Main Tab'}</span>
          <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      </motion.div>
    </div>
  );
};
